import type { PerformanceClinic } from '@/entities/performance/api/performance.api';
import { monthOf } from '@/entities/performance/lib/performance';
import { EmptyState } from '@/shared/components/StateViews';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useLoadMoreSentinel } from '@/shared/hooks/useLoadMoreSentinel';
import { cn } from '@/shared/lib/utils';
import { SearchInput } from '@/shared/components/ui/input';
import { formatWon } from '@/shared/utils/format';
import { Loader2 } from 'lucide-react';

/** 둘째 줄 — 확정 금액, 없으면 확인 중(검수 중 이미지 있음) · 정산 전 (노랑) */
const clinicAmount = (c: PerformanceClinic) => {
  if (c.hasSettlement)
    return { text: formatWon(c.confirmedAmount), pending: false };
  if (c.pendingImageCount > 0) return { text: '확인 중', pending: true };
  return { text: '정산 전', pending: true };
};

/**
 * M-09 좌측 병의원 리스트 (container/left · 실적_월정산_목록 789:11033)
 * 검색 · 정렬(예상↓ → 확정↓) · 페이징은 서버 (GET /monthly/{month}/clinics). 목록 끝이 보이면 다음 페이지.
 * 항목: 기본 · 호버(회색) · 선택(연파랑 + 파랑 Bold 이름) · 정산 전(노랑 글자)
 */
export const ClinicList = ({
  ym,
  clinics,
  loading,
  keyword,
  onKeywordChange,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  selectedId,
  onSelect,
  className,
}: {
  ym: string;
  clinics: PerformanceClinic[];
  totalCount?: number;
  loading: boolean;
  keyword: string;
  onKeywordChange: (keyword: string) => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => unknown;
  selectedId?: number;
  onSelect: (clinicId: number) => void;
  className?: string;
}) => {
  const sentinelRef = useLoadMoreSentinel({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <aside
      className={cn(
        'flex flex-col gap-4 rounded-20 bg-surface-white p-6',
        className,
      )}
    >
      <p className="text-body-15-sb text-fg-primary">
        {monthOf(ym)}월 실적 병의원
      </p>
      <SearchInput
        value={keyword}
        onChange={(e) => onKeywordChange(e.target.value)}
        placeholder="병의원명 검색"
        aria-label="병의원명 검색"
      />
      <ul className="flex flex-col gap-2">
        {loading &&
          Array.from({ length: 6 }, (_, i) => (
            <li key={i}>
              <Skeleton className="h-[88px] w-full rounded-20" />
            </li>
          ))}
        {!loading && clinics.length === 0 && (
          <li>
            <EmptyState
              message={keyword ? '검색 결과가 없어요.' : '병의원이 없어요.'}
              className="py-6"
            />
          </li>
        )}
        {clinics.map((c) => {
          const selected = c.clinicId === selectedId;
          const amount = clinicAmount(c);
          return (
            <li key={c.clinicId}>
              <button
                type="button"
                onClick={() => onSelect(c.clinicId)}
                aria-current={selected || undefined}
                className={cn(
                  'flex w-full flex-col gap-2 rounded-20 p-4 text-left outline-none transition-colors focus-visible:shadow-[0_0_0_3px_var(--color-brand-200)]',
                  selected ? 'bg-brand-100' : 'hover:bg-surface-grey',
                )}
              >
                <span
                  className={cn(
                    'w-full truncate text-body-16-sb',
                    selected ? 'font-bold text-fg-accent' : 'text-fg-primary',
                  )}
                >
                  {c.clinicName}
                </span>
                <span
                  className={cn(
                    'text-body-15-sb',
                    amount.pending ? 'text-fg-warning' : 'text-fg-primary',
                  )}
                >
                  {amount.text}
                </span>
              </button>
            </li>
          );
        })}
        {hasNextPage && (
          <li
            ref={sentinelRef}
            className="flex justify-center py-3"
            aria-hidden
          >
            {isFetchingNextPage && (
              <Loader2 className="size-4 animate-spin text-fg-light" />
            )}
          </li>
        )}
      </ul>
    </aside>
  );
};
